import { useEffect } from "react";

/**
 * Local-market rollout: repoint the site-wide quick quote links (which still
 * go to restorehair.com everywhere else) at our own funnel.
 *
 * Currently active for: Charlotte, Denver, Dallas, Chicago.
 *
 *  - Every quick quote CTA -> /quick-quote-part-1/. Part 1 shows the Norwood
 *    picker when no class is in session storage. /quick-quote-norwood-page-1/
 *    is only a redirect to part 1, so never link to it.
 *
 * Done in the DOM so no shared component has to learn about the rollout.
 */
export function useLocalQuoteLinks(options?: { allToNorwoodPage?: boolean; allToPart1?: boolean }) {
  const allToNorwoodPage = options?.allToNorwoodPage ?? false;
  const allToPart1 = options?.allToPart1 ?? false;
  useEffect(() => {
    const rewrite = () => {
      const links = document.querySelectorAll<HTMLAnchorElement>(
        'a[href*="quick-quote-part-1"], a[href*="quick-quote-norwood-page-1"]',
      );
      links.forEach((a) => {
        // Absolute URL so the destination is the LP domain no matter where
        // the link was originally pointing. Both options now send every CTA,
        // Norwood tiles included, straight to part 1.
        const targetPath =
          allToPart1 || allToNorwoodPage
            ? "https://restorehair.com/quick-quote-part-1/"
            : "/quick-quote-part-1/";

        let url: URL;
        try {
          url = new URL(a.href, window.location.origin);
        } catch {
          return;
        }
        // Already pointing at the right local path (params may have been
        // appended by the UTM decorator) — leave it alone.
        if (targetPath.startsWith("/") && url.origin === window.location.origin && url.pathname === targetPath) return;
        if (!targetPath.startsWith("/") && a.href.startsWith(targetPath)) return;
        // Keep any tracking params that were already on the link.
        const search = url.origin === window.location.origin ? url.search : "";
        a.setAttribute("href", targetPath + search);
      });
    };

    rewrite();
    const observer = new MutationObserver(rewrite);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [allToNorwoodPage, allToPart1]);
}
